import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

import {
  CustomSectionTitle,
  CustomSingleSelect,
  CustomButton,
  CardTab,
  CustomModal,
} from "@ippon/shared-components";
import { CustomListToolbar } from "../../ListToolbar/CustomListToolbar";
import RepairsTable, {
  type RepairRow,
  type RepairStatus,
} from "../../RepairsTable";
import RepairDetailDrawer from "./RepairDetailDrawer";
import RepairCreateDrawer from "./RepairCreateDrawer";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../../../store";
import { Pagination } from "../../PropertyList/Pagination";
import { fetchCategoryManagement } from "../../../store/categoryManagementSlice";
import {
  fetchRepairsByProperty,
  deleteRepair,
  patchRepairStatus,
  updateRepairStatusInList,
} from "../../../store/repairSlice";
import { FILTER_FETCH_LIMIT } from "../../../constants/listFilter";
import { useFilteredPagination } from "../../../hooks/useFilteredPagination";

import { useForm } from "react-hook-form";
import { type RepairTargetInfo } from "../../PropertyList/RegisterRepair/RepairWorkCreateModal";
import { useGlobalToast } from "../../../hooks/useGlobalToast";
import { useCan } from "@/hooks/useCan";
import { AREA_TYPE_LABELS } from "@ippon/shared-types";
import { getApiErrorType, type ApiErrorType } from "@/utils/apiErrorType";
import { useStaleInitialLoadErrorToast } from "@/utils/initialLoadError";

type RepairFormValues = {
  title: string;
  scope: string; // 共用部/専有部
  area: string; // エリア名
  roomNumber: string; // 部屋番号
  categories: string[]; // カテゴリ
  expenditure_type?: string; // capex/opex
  repair_images?: any[];
  estimate_images?: any[];
};

/** タブごとの検索フィルタ用（CustomListToolbar の keyword_${key}, category_${key}） */
type FilterFormValues = {
  [key: `keyword_${string}`]: string;
  [key: `category_${string}`]: string | { value: string; label: string };
};

type PerPageForm = { perPage: number };

const initialCategoryOptions = [
  "衛生器具",
  "外装材",
  "躯体等",
  "建具類",
  "外部金物等",
  "自動ドア・シャッター等",
  "内装",
  "外構（舗装等）",
  "変電設備",
  "動力盤、電灯盤、ケーブル等",
  "電灯コンセント及び配線",
  "照明設備",
  "電話・TV・放送設備等",
  "発電機・蓄電池等",
  "空調換気設備",
  "受水槽・高架水槽・ポンプ",
  "給水設備",
  "給湯設備",
  "排水設備",
  "配管設備",
  "昇降機設備（ELV）",
  "機械式駐車場",
  "消火設備",
  "警報設備",
  "避雷設備",
  "その他",
  "屋上防水・屋根",
  "防火設備",
  "消防設備点検（複合）",
  "特定建築物定期検査（複合）",
  "防火設備点検（複合）",
  "建築設備定期検査（複合）",
  "廃棄物処理",
  "非常照明（建築設備、特建、消防）",
  "誘導灯",
  "法定点検実施費用",
  "排煙設備",
  "リニューアル工事",
  "原状回復工事",
];

interface PropertyRepairsListProps {
  propertyId?: string;
  propertyName?: string;
}

const PropertyRepairsList: React.FC<PropertyRepairsListProps> = ({
  propertyId,
  propertyName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const repairsList = useSelector((s: RootState) => s.repairs.list.data);
  const total = useSelector((s: RootState) => s.repairs.list.total);
  const loading = useSelector((s: RootState) => s.repairs.list.loading);
  const repairsLoaded = useSelector((s: RootState) => s.repairs.list.loaded);
  const { showToast } = useGlobalToast();
  const can = useCan();

  // ページネーションの状態
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(50);

  // CardTab: すべて / 専有部 / 共用部 / 建物設備 / 敷地 / その他
  // 値は新スキーマの AreaType (Unit.area_type) に合わせる。
  const [cardTabActive, setCardTabActive] = useState(0);
  const cardTabAreaType = React.useMemo<string | null>(() => {
    if (cardTabActive === 1) return "private";
    if (cardTabActive === 2) return "common";
    if (cardTabActive === 3) return "facility";
    if (cardTabActive === 4) return "land";
    if (cardTabActive === 5) return "other";
    return null; // 「すべて」
  }, [cardTabActive]);

  const perPageOptions = React.useMemo(
    () => [
      { value: 10, label: "10件" },
      { value: 25, label: "25件" },
      { value: 50, label: "50件" },
      { value: 100, label: "100件" },
    ],
    [],
  );

  // 修繕工事登録モーダルの状態
  const [openRepairModal, setOpenRepairModal] = useState(false);
  const [_targetInfo, setTargetInfo] = useState<RepairTargetInfo>({});
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  // Notion 風プレビュードロワーの状態
  const [previewRepairId, setPreviewRepairId] = useState<string | null>(null);
  const [selectedRepairIds, setSelectedRepairIds] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<{
    keyword: string;
    category: string | null;
  }>({
    keyword: "",
    category: null,
  });

  const {
    register,
    control,
    formState: { errors },
    setValue,
    reset,
    getValues,
  } = useForm<RepairFormValues & FilterFormValues>({
    defaultValues: {
      title: "",
      scope: "",
      area: "",
      roomNumber: "",
      categories: [],
      repair_images: [],
      estimate_images: [],
    },
  });

  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });

  // 修繕ごとのステータス変更（楽観的更新）
  const handlePatchStatus = async (
    id: string | number,
    status: RepairStatus,
  ) => {
    const repairId = String(id);
    const previousItem = repairsList.find((r: any) => r.repair_id === repairId);
    const previousStatus = previousItem?.status;

    // ローカルステートを即座に更新（UI反映）
    dispatch(updateRepairStatusInList({ repair_id: repairId, status }));

    try {
      await dispatch(
        patchRepairStatus({ repair_id: repairId, status }),
      ).unwrap();
      showToast({ message: "ステータスを更新しました", type: "success" });
    } catch (_e) {
      // 失敗時はローカルステートを元に戻す
      if (previousStatus) {
        dispatch(
          updateRepairStatusInList({
            repair_id: repairId,
            status: previousStatus,
          }),
        );
      }
      showToast({ message: "ステータス更新に失敗しました", type: "error" });
      throw _e;
    }
  };

  const [categoryOptions, setCategoryOptions] = React.useState<
    { label: string; value: string }[]
  >(initialCategoryOptions.map((v) => ({ label: v, value: v })));
  const { record: categoryRecord } = useSelector(
    (s: RootState) => s.categoryManagement,
  );

  // カテゴリ管理を取得してマージ
  React.useEffect(() => {
    dispatch(fetchCategoryManagement())
      .unwrap()
      .then((rec: any) => {
        if (rec && Array.isArray(rec.categories)) {
          const mergedValues = Array.from(
            new Set([...initialCategoryOptions, ...rec.categories]),
          );
          setCategoryOptions(
            mergedValues.map((v) => ({ label: String(v), value: String(v) })),
          );
        }
      })
      .catch(() => {
        // 失敗時は初期カテゴリをそのまま使用
      });
  }, [dispatch]);

  // ストアのカテゴリ管理レコードが更新されたら同期
  React.useEffect(() => {
    if (categoryRecord && Array.isArray(categoryRecord.categories)) {
      const mergedValues = Array.from(
        new Set([...initialCategoryOptions, ...categoryRecord.categories]),
      );
      setCategoryOptions(
        mergedValues.map((v) => ({ label: String(v), value: String(v) })),
      );
    }
  }, [categoryRecord]);

  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );

  // CardTab の件数とテーブルの絞り込みを同じ全件データから算出する。
  React.useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      setInitialLoadError(null);
      try {
        const action = await dispatch(
          fetchRepairsByProperty({
            property_id: propertyId,
            page: 1,
            limit: FILTER_FETCH_LIMIT,
          }),
        );
        if (cancelled) return;
        if ((action as { type?: string }).type?.endsWith("/rejected")) {
          const payload = (action as any).payload as
            | { errorType?: ApiErrorType }
            | undefined;
          setInitialLoadError(
            payload?.errorType ??
              getApiErrorType((action as any).error ?? action) ??
              "unknown",
          );
        }
      } catch (err) {
        if (!cancelled) setInitialLoadError(getApiErrorType(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, propertyId]);

  const hasRepairsCache = repairsLoaded || repairsList.length > 0;
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasRepairsCache,
    dedupeKey: `repairs:${propertyId ?? ""}`,
  });

  // ページ変更ハンドラー
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 表示件数変更ハンドラー
  const handlePerPageChange = (value: number) => {
    setPerPage(value);
    setCurrentPage(1); // 1ページ目に戻す
  };

  // 修繕工事の登録処理は /repairs/new ページに移管済み。

  // 修繕工事登録モーダルを開く
  const handleOpenRepairModal = () => {
    setTargetInfo({});
    setOpenRepairModal(true);
  };

  // 修繕工事登録モーダルを閉じる
  const handleCloseRepairModal = () => {
    setOpenRepairModal(false);
    setTargetInfo({});
    reset();
  };

  // 修繕削除モーダルを開く
  const handleOpenDeleteModal = () => {
    if (selectedRepairIds.size === 0) {
      showToast({
        type: "warning",
        message: "削除する修繕を選択してください",
      });
      return;
    }
    setOpenDeleteModal(true);
  };

  // 修繕削除処理
  const handleDeleteRepairs = async () => {
    setOpenDeleteModal(false);
    setIsDeleting(true);

    try {
      const deletePromises = Array.from(selectedRepairIds).map(
        async (repairId) => {
          const action = await dispatch(
            deleteRepair({ repair_id: repairId }) as any,
          );
          return action;
        },
      );

      const results = await Promise.all(deletePromises);

      // 全ての削除が成功したかチェック
      const successfulResults = results.filter(
        (result) => result.type === "repairs/delete/fulfilled",
      );
      const failedResults = results.filter(
        (result) => result.type === "repairs/delete/rejected",
      );

      if (successfulResults.length === results.length) {
        showToast({
          type: "success",
          message: `${selectedRepairIds.size}件の修繕を削除しました`,
        });

        // 選択状態をクリア
        setSelectedRepairIds(new Set());

        // 修繕リストを再取得（1ページ目に戻る）
        setCurrentPage(1);
        if (propertyId) {
          dispatch(
            fetchRepairsByProperty({
              property_id: propertyId,
              page: 1,
              limit: FILTER_FETCH_LIMIT,
            }),
          );
        }
      } else {
        // 失敗した結果の詳細を表示
        const errorMessages = failedResults.map((result) => {
          const error = result.payload as any;
          return error?.message || "削除に失敗しました";
        });

        console.error("Delete failed results:", failedResults);

        showToast({
          type: "error",
          message: `${failedResults.length}件の修繕の削除に失敗しました: ${errorMessages.join(", ")}`,
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast({
        type: "error",
        message: "工事の削除中にエラーが発生しました",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const mappedRows: RepairRow[] = React.useMemo(() => {
    const items = Array.isArray(repairsList) ? repairsList : [];
    return items.map((r: any) => ({
      id: r.repair_id,
      title: r.repair_title,
      expenditureType: r.expenditure_type || "",
      areaType: r.area_type,
      detail: r.area_name,
      category: Array.isArray(r.category) ? r.category.join(", ") : r.category,
      status: (r.status as any) || "未対応",
      registeredAt: r.created_at,
      estimates: Array.isArray(r.repair_estimates)
        ? r.repair_estimates.map((e: any) => ({
            id: e.repair_estimate_id,
            companyName: e.estimate_company_name || "",
            status: (e.status as any) || "待機中",
            amount: e.estimate_amount ?? null,
            thumbnailUrl: e.repair_image,
          }))
        : [],
      // 追加: 完全なデータを保持
      repair_estimates: r.repair_estimates || [],
      repair_images: r.repair_images || [],
      rawData: r, // 元のAPIレスポンスデータ全体
    }));
  }, [repairsList]);

  const filteredRows: RepairRow[] = React.useMemo(() => {
    const keyword = (appliedFilters.keyword || "").trim().toLowerCase();
    const category = appliedFilters.category || "";
    const areaType = cardTabAreaType || "";
    return mappedRows.filter((row) => {
      const matchKeyword = keyword
        ? (row.title || "").toLowerCase().includes(keyword)
        : true;
      const categoryText = typeof row.category === "string" ? row.category : "";
      const matchCategory = category ? categoryText.includes(category) : true;
      const matchAreaType = areaType
        ? (row as any).areaType === areaType
        : true;
      return matchKeyword && matchCategory && matchAreaType;
    });
  }, [mappedRows, appliedFilters, cardTabAreaType]);

  // CardTab 件数: area_type 別にカウント (category/keyword フィルターは無視してタブ件数は全体ベース)
  const tabTotals = React.useMemo(() => {
    const counts = {
      all: 0,
      private: 0,
      common: 0,
      facility: 0,
      land: 0,
      other: 0,
    };
    for (const r of mappedRows) {
      counts.all += 1;
      const at = (r as any).areaType as string | undefined;
      if (at === "private") counts.private += 1;
      else if (at === "common") counts.common += 1;
      else if (at === "facility") counts.facility += 1;
      else if (at === "land") counts.land += 1;
      else if (at === "other") counts.other += 1;
    }
    return counts;
  }, [mappedRows]);

  const { paginatedRows, totalPages } = useFilteredPagination({
    hasActiveFilter: true,
    rows: filteredRows,
    serverTotal: total || 0,
    currentPage,
    perPage,
  });

  const cardTabs = [
    {
      key: "all",
      label: "すべて",
      count: tabTotals.all,
      hasNotification: false,
    },
    {
      key: "private",
      label: AREA_TYPE_LABELS.private,
      count: tabTotals.private,
      hasNotification: false,
    },
    {
      key: "common",
      label: AREA_TYPE_LABELS.common,
      count: tabTotals.common,
      hasNotification: false,
    },
    {
      key: "facility",
      label: AREA_TYPE_LABELS.facility,
      count: tabTotals.facility,
      hasNotification: false,
    },
    {
      key: "land",
      label: AREA_TYPE_LABELS.land,
      count: tabTotals.land,
      hasNotification: false,
    },
    {
      key: "other",
      label: AREA_TYPE_LABELS.other,
      count: tabTotals.other,
      hasNotification: false,
    },
  ];

  const cardTabData = {
    all: {
      data: [],
      pagination: { total: tabTotals.all, page: 1, limit: perPage },
    },
    private: {
      data: [],
      pagination: { total: tabTotals.private, page: 1, limit: perPage },
    },
    common: {
      data: [],
      pagination: { total: tabTotals.common, page: 1, limit: perPage },
    },
    facility: {
      data: [],
      pagination: { total: tabTotals.facility, page: 1, limit: perPage },
    },
    land: {
      data: [],
      pagination: { total: tabTotals.land, page: 1, limit: perPage },
    },
    other: {
      data: [],
      pagination: { total: tabTotals.other, page: 1, limit: perPage },
    },
  };

  return (
    <Box pb={{ xs: 2, md: 10 }}>
      <Box sx={{ mb: 2 }}>
        <CustomSectionTitle title="工事管理" />
      </Box>
      <CardTab
        tabs={cardTabs}
        tabData={cardTabData}
        activeTab={cardTabActive}
        loading={loading}
        loadingDelayMs={150}
        onTabChange={(tabIndex: number) => {
          setCardTabActive(tabIndex);
          setCurrentPage(1);
          setSelectedRepairIds(new Set());
        }}
        renderContent={(_data, key) => (
          <Box>
            {/* CardTab / 検索バー / テーブルの間に 8px ずつ余白を入れる。
                CustomListToolbar は内部 Stack に mt={1} (8px) を持つため
                上側はそのまま利用し、下側 (テーブルとの間) を mb: 1 で追加。 */}
            <Box sx={{ mb: 1 }}>
              <CustomListToolbar
                responsiveBreakpoint="md"
                showSearch={true}
                searchPlaceholder="工事名で絞り込む"
                searchRegister={register}
                searchErrors={errors}
                searchName={`keyword_${key}`}
                onSearch={() => {
                  const kw = (
                    getValues(`keyword_${key}` as keyof FilterFormValues) || ""
                  ).toString();
                  const catValue = getValues(
                    `category_${key}` as keyof FilterFormValues,
                  );
                  const category =
                    typeof catValue === "string"
                      ? catValue
                      : ((catValue as { value?: string })?.value ?? "");
                  setAppliedFilters({
                    keyword: kw,
                    category: category || null,
                  });
                  setCurrentPage(1);
                }}
                onClear={() => {
                  setValue(`keyword_${key}` as keyof FilterFormValues, "");
                  setValue(`category_${key}` as keyof FilterFormValues, "");
                  setAppliedFilters({ keyword: "", category: null });
                  setCurrentPage(1);
                }}
                leftExtra={
                  <Box sx={{ width: { xs: "100%", md: 140 } }}>
                    <CustomSingleSelect
                      label=""
                      name={`category_${key}`}
                      control={control}
                      options={categoryOptions}
                      labelWidth="0"
                      labelWidthSp="0"
                      inputWidth="100%"
                      inputWidthSp="100%"
                      placeholder="カテゴリ名"
                      autoMenuWidth
                      hasSpacing={false}
                    />
                  </Box>
                }
                perPageOptions={perPageOptions}
                perPageControl={perPageControl}
                onPerPageChange={handlePerPageChange}
                primaryButtonLabel="工事登録"
                primaryButtonOnClick={handleOpenRepairModal}
                showPrimaryButton={can("repair:edit")}
                deleteDisabled={isDeleting || selectedRepairIds.size === 0}
                showDelete={can("repair:delete")}
                onDeleteClick={handleOpenDeleteModal}
              />
            </Box>

            {/* 修繕テーブル: ヘッダーは常時表示し、ローディングはヘッダー下のみで行う
                (物件構成一覧と同じ挙動)。RepairsTable 自身が loading prop を受けて
                ヘッダー下にだけ CustomLoading を出す。 */}
            <RepairsTable
              rows={paginatedRows}
              onSelectionChange={(ids) => {
                setSelectedRepairIds(new Set(ids.map(String)));
              }}
              onPatchStatus={handlePatchStatus}
              propertyId={propertyId}
              propertyName={propertyName}
              fromSource="repairs"
              onRowPreview={(row) => setPreviewRepairId(String(row.id))}
              loading={loading}
            />
            {!loading && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </Box>
        )}
      />

      {/* 修繕工事 新規登録モーダル（旧: PreviewDrawer + /repairs/new ページ埋め込み） */}
      {propertyId && (
        <RepairCreateDrawer
          open={openRepairModal}
          onClose={handleCloseRepairModal}
          propertyId={propertyId}
          // CardTab で「すべて」以外を選択中なら、その場所区分を新規登録の初期値に流す
          initialScope={
            (cardTabAreaType as
              | "private"
              | "common"
              | "facility"
              | "land"
              | "other"
              | null) ?? undefined
          }
          onCreated={() => {
            dispatch(
              fetchRepairsByProperty({
                property_id: propertyId,
                page: 1,
                limit: FILTER_FETCH_LIMIT,
              }),
            );
          }}
        />
      )}

      {/* 修繕削除確認モーダル */}
      <CustomModal
        title="削除の確認"
        openModal={openDeleteModal}
        handleCloseModal={() => setOpenDeleteModal(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedRepairIds.size}
            件の修繕情報を削除してもよろしいでしょうか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setOpenDeleteModal(false)}
          />
          <CustomButton
            label="削除"
            onClick={handleDeleteRepairs}
            buttonCategory="deleteModal"
            disabled={isDeleting}
          />
        </Box>
      </CustomModal>

      {/* Notion 風プレビュードロワー (修繕詳細の要約表示) */}
      <RepairDetailDrawer
        open={!!previewRepairId}
        onClose={() => setPreviewRepairId(null)}
        repairId={previewRepairId}
        propertyId={propertyId}
        propertyName={propertyName}
      />
    </Box>
  );
};

export default PropertyRepairsList;
